import { motion } from "framer-motion";
import { Check, Sparkles, Heart } from "lucide-react";
import { Reveal, SectionTitle } from "@/components/site/Reveal";
import { scrollToId } from "@/lib/scroll";
import { PRESTATIONS, PARFUMS, AVANTAGES, ETAPES, TEMOIGNAGES } from "@/data/content";

export const Prestations = () => (
  <section id="prestations" className="py-20 sm:py-28 px-5 sm:px-8 bg-cream">
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <SectionTitle
          overline="Nos prestations"
          title="Un stand de glace pour chaque événement"
          subtitle="Du mariage élégant au festival animé, Moum's s'invite partout où il y a de la joie à partager."
        />
      </Reveal>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {PRESTATIONS.map((p, i) => (
          <Reveal key={p.titre} delay={(i % 3) * 0.08}>
            <div data-testid={`prestation-${i}`} className="h-full rounded-3xl bg-white border border-beige p-7 shadow-soft hover:shadow-hover hover:-translate-y-1 transition-all duration-300">
              <div className="h-11 w-11 rounded-2xl bg-pastelbg flex items-center justify-center mb-4">
                <Sparkles size={20} className="text-strawberry" />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-chocolate">{p.titre}</h3>
              <p className="mt-2 text-cocoa leading-relaxed">{p.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <div className="mt-12 rounded-3xl bg-chocolate text-cream p-10 sm:p-14 text-center relative overflow-hidden grain">
          <Heart className="mx-auto text-pastel mb-4" />
          <h3 className="font-heading text-3xl sm:text-4xl font-medium">Chaque prestation est entièrement personnalisée</h3>
          <p className="mt-4 max-w-2xl mx-auto text-cream/80 leading-relaxed">
            Parfums, toppings, coulis, décoration du stand, durée, nombre d'invités… Nous construisons
            votre animation glacée selon vos envies et votre événement. Vous rêvez, on réalise.
          </p>
          <button data-testid="perso-devis-btn" onClick={() => scrollToId("devis")} className="mt-7 inline-flex rounded-full bg-strawberry px-8 py-4 font-semibold text-white hover:bg-[#B83044] hover:scale-105 transition-all">
            Obtenir mon devis
          </button>
        </div>
      </Reveal>
    </div>
  </section>
);

export const Parfums = () => (
  <section id="parfums" className="py-20 sm:py-28 px-5 sm:px-8">
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <SectionTitle overline="Nos parfums" title="Trois saveurs signature" subtitle="Onctueuses, généreuses et servies à la minute pour un plaisir intense." />
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {PARFUMS.map((f, i) => (
          <Reveal key={f.nom} delay={i * 0.1}>
            <motion.div whileHover={{ scale: 1.03 }} data-testid={`parfum-${f.nom.toLowerCase()}`} className="group rounded-[2rem] overflow-hidden bg-white border border-beige shadow-soft">
              <div className="relative h-72 overflow-hidden">
                <img src={f.img} alt={`Glace à l'italienne ${f.nom}`} className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <span className="absolute top-4 left-4 text-4xl drop-shadow-md">{f.emoji}</span>
              </div>
              <div className="p-7" style={{ borderTop: `4px solid ${f.accent}` }}>
                <h3 className="font-heading text-3xl font-semibold text-chocolate">{f.nom}</h3>
                <p className="mt-2 text-cocoa leading-relaxed">{f.desc}</p>
              </div>
            </motion.div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const Avantages = () => (
  <section id="pourquoi" className="py-20 sm:py-28 px-5 sm:px-8 bg-pastelbg">
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <SectionTitle overline="Pourquoi Moum's ?" title="Une prestation sérieuse et gourmande" subtitle="Tout ce qu'il faut pour un événement réussi, sans stress." />
      </Reveal>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {AVANTAGES.map((a, i) => (
          <Reveal key={a.titre} delay={(i % 3) * 0.08}>
            <div data-testid={`avantage-${i}`} className="flex gap-4 rounded-3xl bg-white p-6 shadow-soft border border-white h-full">
              <div className="shrink-0 h-10 w-10 rounded-full bg-strawberry flex items-center justify-center">
                <Check size={18} className="text-white" />
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold text-chocolate">{a.titre}</h3>
                <p className="mt-1 text-sm text-cocoa leading-relaxed">{a.desc}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const Etapes = () => (
  <section className="py-20 sm:py-28 px-5 sm:px-8 bg-cream">
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <SectionTitle overline="Comment ça marche ?" title="Votre parcours en 5 étapes" subtitle="Simple, transparent et sans engagement jusqu'à la validation de votre devis." />
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-5">
        {ETAPES.map((e, i) => (
          <Reveal key={e.n} delay={i * 0.08}>
            <div data-testid={`etape-${i}`} className="rounded-3xl bg-white border border-beige p-6 h-full shadow-soft">
              <span className="font-heading text-4xl font-semibold text-pastel">{e.n}</span>
              <h3 className="mt-2 font-heading text-xl font-semibold text-chocolate">{e.titre}</h3>
              <p className="mt-1 text-sm text-cocoa">{e.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const Temoignages = () => (
  <section className="py-20 sm:py-28 px-5 sm:px-8">
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <SectionTitle overline="Ils nous ont fait confiance" title="Ce que disent nos clients" />
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {TEMOIGNAGES.map((t, i) => (
          <Reveal key={t.nom} delay={i * 0.1}>
            <div data-testid={`temoignage-${i}`} className="rounded-3xl bg-white border border-beige p-8 shadow-soft h-full">
              <div className="text-strawberry text-2xl">★★★★★</div>
              <p className="mt-4 text-cocoa italic leading-relaxed">« {t.texte} »</p>
              <div className="mt-5">
                <p className="font-heading text-lg font-semibold text-chocolate">{t.nom}</p>
                <p className="text-sm text-muted-foreground">{t.event}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
